import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FabricCanvas } from '../../types/fabric';
import { addImageFitted } from '../../lib/canvasObjectHelpers';
import { useToast } from '../Notifications';
import { useDesignStore } from '../../store/designStore';

interface QRCodeToolProps {
  canvas: FabricCanvas | null;
}

export function QRCodeTool({ canvas }: QRCodeToolProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const addToCanvas = async () => {
    const value = text.trim();
    if (!canvas || !value) return;
    setBusy(true);
    // See designStore.ts's own pendingCanvasCommits comment for the real bug
    // this closes — fabric.Image.fromURL below decodes asynchronously before
    // its callback actually mutates the canvas; without this guard, a
    // side/tab switch could dispose the canvas out from under that pending
    // callback, silently losing the QR code that was about to be placed.
    useDesignStore.getState().beginCanvasCommit();
    try {
      const [QRCode, { fabric }] = await Promise.all([import('qrcode'), import('fabric')]);
      const dataUrl = await QRCode.toDataURL(value, { margin: 1, width: 512 });
      await new Promise<void>(resolve => {
        fabric.Image.fromURL(dataUrl, (img: any) => {
          addImageFitted(canvas, img);
          resolve();
        });
      });
    } catch {
      showToast(t('qrcode.failed'), 'error');
    } finally {
      setBusy(false);
      useDesignStore.getState().endCanvasCommit();
    }
  };

  return (
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
        {t('qrcode.description')}
      </p>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={t('qrcode.placeholder')}
        rows={3}
        style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', border: '1px solid var(--border)', borderRadius: 6, padding: 8, fontSize: 12 }}
      />
      <button
        onClick={addToCanvas}
        disabled={!text.trim() || busy}
        className="btn btn-secondary"
        style={{ fontSize: 12, width: '100%' }}
      >
        {busy ? t('qrcode.generating') : t('qrcode.addToCanvas')}
      </button>
    </div>
  );
}
